import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, X } from "lucide-react";
import api from "@/lib/api";
import { Section, DeleteBtn } from "./common";
import { TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_STANDING = { id: null, team: "", played: 0, won: 0, lost: 0, no_result: 0, points: 0 };
const EMPTY_FIXTURE = { id: null, team_a: "", team_b: "", date: "", time: "", venue: "", status: "scheduled", winner: "", result: "", score_a_runs: 0, score_a_overs: "", score_b_runs: 0, score_b_overs: "" };

export default function SeasonTab() {
  const [standings, setStandings] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [sForm, setSForm] = useState(EMPTY_STANDING);
  const [fForm, setFForm] = useState(EMPTY_FIXTURE);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, f] = await Promise.all([api.get("/standings"), api.get("/fixtures")]);
      setStandings(s.data);
      setFixtures(f.data);
    } catch {
      toast.error("Failed to load season data");
    }
  };

  useEffect(() => { load(); }, []);

  const saveStanding = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { team: sForm.team, played: +sForm.played || 0, won: +sForm.won || 0, lost: +sForm.lost || 0, no_result: +sForm.no_result || 0, points: +sForm.points || 0 };
      if (sForm.id) await api.put(`/standings/${sForm.id}`, body);
      else await api.post("/standings", body);
      toast.success("Points table updated");
      setSForm(EMPTY_STANDING);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const saveFixture = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        team_a: fForm.team_a, team_b: fForm.team_b, date: fForm.date, time: fForm.time, venue: fForm.venue,
        status: fForm.status, winner: fForm.winner, result: fForm.result,
        score_a_runs: +fForm.score_a_runs || 0, score_a_overs: fForm.score_a_overs || "0.0",
        score_b_runs: +fForm.score_b_runs || 0, score_b_overs: fForm.score_b_overs || "0.0",
      };
      if (fForm.id) await api.put(`/fixtures/${fForm.id}`, body);
      else await api.post("/fixtures", body);
      toast.success("Fixture saved");
      setFForm(EMPTY_FIXTURE);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const numField = (form, setForm, key, label, testid) => (
    <div>
      <Label>{label}</Label>
      <Input type="number" min="0" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="bg-white" data-testid={testid} />
    </div>
  );

  return (
    <TabsContent value="season" data-testid="admin-season-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={sForm.id ? `Edit — ${sForm.team}` : "Add a team to the table"}>
          <form onSubmit={saveStanding} className="space-y-4" data-testid="admin-standing-form">
            <div>
              <Label htmlFor="st-team">Team name</Label>
              <Input id="st-team" value={sForm.team} onChange={(e) => setSForm({ ...sForm, team: e.target.value })} required className="bg-white" data-testid="admin-standing-team" />
            </div>
            <div className="grid grid-cols-5 gap-2">
              {numField(sForm, setSForm, "played", "P", "admin-standing-played")}
              {numField(sForm, setSForm, "won", "W", "admin-standing-won")}
              {numField(sForm, setSForm, "lost", "L", "admin-standing-lost")}
              {numField(sForm, setSForm, "no_result", "NR", "admin-standing-nr")}
              {numField(sForm, setSForm, "points", "Pts", "admin-standing-points")}
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} data-testid="admin-standing-submit" className="gap-2"><Plus size={15} /> {sForm.id ? "Update" : "Add team"}</Button>
              {sForm.id && <Button type="button" variant="outline" onClick={() => setSForm(EMPTY_STANDING)} data-testid="admin-standing-cancel" className="gap-2"><X size={14} /> Cancel</Button>}
            </div>
          </form>
        </Section>
        <Section title={`Points table (${standings.length})`}>
          <ul className="divide-y divide-black/5">
            {standings.map((s) => (
              <li key={s.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-standing-item-${s.id}`}>
                <p className="text-sm font-semibold text-league-ink">
                  {s.team} <span className="text-muted-foreground font-normal">— {s.played}P · {s.won}W · {s.lost}L · {s.no_result}NR{typeof s.nrr === "number" ? ` · NRR ${s.nrr > 0 ? "+" : ""}${s.nrr.toFixed(2)}` : ""} · </span><span className="text-league-blue">{s.points} pts</span>
                </p>
                <span className="flex shrink-0">
                  <button onClick={() => setSForm(s)} data-testid={`admin-standing-edit-${s.id}`} className="p-2 rounded-md text-league-blue/70 transition-colors duration-200 hover:bg-league-blue hover:text-white" aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <DeleteBtn testid={`admin-standing-delete-${s.id}`} onClick={async () => { await api.delete(`/standings/${s.id}`).catch(() => toast.error("Delete failed")); load(); }} />
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title={fForm.id ? "Edit fixture" : "Add a fixture"}>
          <form onSubmit={saveFixture} className="space-y-4" data-testid="admin-fixture-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fx-a">Team A</Label>
                <Input id="fx-a" value={fForm.team_a} onChange={(e) => setFForm({ ...fForm, team_a: e.target.value })} required className="bg-white" data-testid="admin-fixture-team-a" />
              </div>
              <div>
                <Label htmlFor="fx-b">Team B</Label>
                <Input id="fx-b" value={fForm.team_b} onChange={(e) => setFForm({ ...fForm, team_b: e.target.value })} required className="bg-white" data-testid="admin-fixture-team-b" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fx-date">Date</Label>
                <Input id="fx-date" type="date" value={fForm.date} onChange={(e) => setFForm({ ...fForm, date: e.target.value })} required className="bg-white" data-testid="admin-fixture-date" />
              </div>
              <div>
                <Label htmlFor="fx-time">Time</Label>
                <Input id="fx-time" value={fForm.time} onChange={(e) => setFForm({ ...fForm, time: e.target.value })} placeholder="7:00 PM" className="bg-white" data-testid="admin-fixture-time" />
              </div>
            </div>
            <div>
              <Label htmlFor="fx-venue">Venue</Label>
              <Input id="fx-venue" value={fForm.venue} onChange={(e) => setFForm({ ...fForm, venue: e.target.value })} placeholder="Lalbhai Contractor Stadium" className="bg-white" data-testid="admin-fixture-venue" />
            </div>
            <div className="grid grid-cols-3 gap-4 items-end">
              <div>
                <Label>Status</Label>
                <Select value={fForm.status} onValueChange={(v) => setFForm({ ...fForm, status: v })}>
                  <SelectTrigger className="bg-white" data-testid="admin-fixture-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled" data-testid="admin-fixture-status-scheduled">Upcoming</SelectItem>
                    <SelectItem value="live" data-testid="admin-fixture-status-live">Live</SelectItem>
                    <SelectItem value="completed" data-testid="admin-fixture-status-completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Winner</Label>
                <Select value={fForm.winner || "none"} onValueChange={(v) => setFForm({ ...fForm, winner: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-white" data-testid="admin-fixture-winner"><SelectValue placeholder="No result / tie" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="admin-fixture-winner-none">No result / tie</SelectItem>
                    {fForm.team_a && <SelectItem value={fForm.team_a} data-testid="admin-fixture-winner-a">{fForm.team_a}</SelectItem>}
                    {fForm.team_b && <SelectItem value={fForm.team_b} data-testid="admin-fixture-winner-b">{fForm.team_b}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fx-result">Result text</Label>
                <Input id="fx-result" value={fForm.result} onChange={(e) => setFForm({ ...fForm, result: e.target.value })} placeholder="Strikers won by 6 wickets" className="bg-white" data-testid="admin-fixture-result" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Mark a match Completed and pick the winner — the points table updates itself (win = 2 pts, no-result = 1 pt each).</p>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label>{fForm.team_a || "Team A"} runs</Label>
                <Input type="number" min="0" value={fForm.score_a_runs} onChange={(e) => setFForm({ ...fForm, score_a_runs: e.target.value })} className="bg-white" data-testid="admin-fixture-score-a-runs" />
              </div>
              <div>
                <Label>overs</Label>
                <Input value={fForm.score_a_overs} onChange={(e) => setFForm({ ...fForm, score_a_overs: e.target.value })} placeholder="20.0" className="bg-white" data-testid="admin-fixture-score-a-overs" />
              </div>
              <div>
                <Label>{fForm.team_b || "Team B"} runs</Label>
                <Input type="number" min="0" value={fForm.score_b_runs} onChange={(e) => setFForm({ ...fForm, score_b_runs: e.target.value })} className="bg-white" data-testid="admin-fixture-score-b-runs" />
              </div>
              <div>
                <Label>overs</Label>
                <Input value={fForm.score_b_overs} onChange={(e) => setFForm({ ...fForm, score_b_overs: e.target.value })} placeholder="17.0" className="bg-white" data-testid="admin-fixture-score-b-overs" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Final scores feed the NRR (net run rate) column automatically.</p>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} data-testid="admin-fixture-submit" className="gap-2"><Plus size={15} /> {fForm.id ? "Update" : "Add fixture"}</Button>
              {fForm.id && <Button type="button" variant="outline" onClick={() => setFForm(EMPTY_FIXTURE)} data-testid="admin-fixture-cancel" className="gap-2"><X size={14} /> Cancel</Button>}
            </div>
          </form>
        </Section>
        <Section title={`Fixtures (${fixtures.length})`}>
          <ul className="divide-y divide-black/5">
            {fixtures.map((f) => (
              <li key={f.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-fixture-item-${f.id}`}>
                <div>
                  <p className="text-sm font-semibold text-league-ink">{f.team_a} vs {f.team_b}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{f.date}{f.time ? ` • ${f.time}` : ""}{f.venue ? ` • ${f.venue}` : ""} • {f.status}</p>
                </div>
                <span className="flex shrink-0">
                  <button onClick={() => setFForm(f)} data-testid={`admin-fixture-edit-${f.id}`} className="p-2 rounded-md text-league-blue/70 transition-colors duration-200 hover:bg-league-blue hover:text-white" aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <DeleteBtn testid={`admin-fixture-delete-${f.id}`} onClick={async () => { await api.delete(`/fixtures/${f.id}`).catch(() => toast.error("Delete failed")); load(); }} />
                </span>
              </li>
            ))}
            {fixtures.length === 0 && <li className="py-4 text-sm text-muted-foreground">No fixtures yet — add the first one when the schedule is final.</li>}
          </ul>
        </Section>
      </div>
    </TabsContent>
  );
}
